<template>
  <div class="container-fluid aiud-screen">
    <page-header :page="page" has-return>
      <template v-slot:customActions>
        <ActionButton
          variant="outline-success m-1"
          text="Actualizar"
          i="fas fa-sync-alt"
          :responsive="false"
          :disabled="loading"
          @click="loadSummary"
        />
      </template>
    </page-header>

    <div v-if="loading" class="aiud-hint">Cargando resumen de uso...</div>
    <div v-else-if="error" class="aiud-error">{{ error }}</div>

    <template v-else-if="summary">
      <div class="aiud-stats mb-4">
        <div class="aiud-stat">
          <div class="aiud-stat-value">{{ summary.totalGenerations }}</div>
          <div class="aiud-stat-label">Generaciones reales</div>
        </div>
        <div class="aiud-stat">
          <div class="aiud-stat-value">{{ summary.totalTestGenerations }}</div>
          <div class="aiud-stat-label">Corridas de Modo de Prueba</div>
        </div>
        <div class="aiud-stat">
          <div class="aiud-stat-value">{{ successRateLabel }}</div>
          <div class="aiud-stat-label">Tasa de éxito (reales)</div>
        </div>
      </div>

      <CustomSeparator text="Tendencia semanal de uso" class="mt-4 custom-separator-line" />

      <CustomResultNotFound
        v-if="summary.tendencia.length === 0"
        class="mt-3"
        text="Todavía no hay generaciones reales registradas."
      />
      <div v-else class="aiud-trend mt-3">
        <div v-for="punto in summary.tendencia" :key="punto.semanaInicio" class="aiud-trend-row">
          <span class="aiud-trend-week">{{ formatWeek(punto.semanaInicio) }}</span>
          <div class="aiud-trend-bar-track">
            <div
              class="aiud-trend-bar"
              :style="{ width: trendBarWidth(punto.totalGenerations) + '%' }"
            ></div>
          </div>
          <span class="aiud-trend-count">{{ punto.totalGenerations }}</span>
        </div>
      </div>

      <CustomSeparator text="Uso por Sección / Área" class="mt-4 custom-separator-line" />
      <p class="aiud-hint">
        Ordenado por feedback negativo (👎), para ver primero lo que más necesita revisión. Cada
        fila suma TODAS las versiones históricas de esa clave, no solo la Publicada hoy.
      </p>

      <CustomResultNotFound
        v-if="summary.porSeccion.length === 0"
        class="mt-3"
        text="Todavía no hay ninguna instrucción registrada."
      />

      <div v-else class="aiud-table mt-3">
        <div class="aiud-table-header">
          <span>Sección / Área</span>
          <span>Nombre descriptivo</span>
          <span>Estado</span>
          <span class="aiud-col-num">Generaciones</span>
          <span class="aiud-col-num">Usadas</span>
          <span class="aiud-col-num">👍</span>
          <span class="aiud-col-num">👎</span>
        </div>

        <div
          v-for="fila in summary.porSeccion"
          :key="fila.aiPromptConfigurationId"
          class="aiud-table-row"
          @click="goToDetail(fila)"
        >
          <span><strong>{{ fila.modulo }}</strong>.{{ fila.seccionArea }}</span>
          <span>{{ fila.nombreDescriptivo || 'Sin nombre' }}</span>
          <span>
            <span class="aiud-status-badge" :class="estadoBadgeClass(fila.estado)">{{ fila.estado }}</span>
          </span>
          <span class="aiud-col-num">{{ fila.totalGenerations }}</span>
          <span class="aiud-col-num">{{ fila.usedCount }}</span>
          <span class="aiud-col-num aiud-positive">{{ fila.positiveFeedbackCount }}</span>
          <span class="aiud-col-num aiud-negative">{{ fila.negativeFeedbackCount }}</span>
          <span v-if="fila.sinFeedbackRegistrado" class="aiud-col-note">
            Sin feedback registrado -- todavía nadie marcó "Usar Esta Versión" ni 👍/👎 aquí.
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import { getUsageSummary } from '@/modules/operation-ai-prompt-management/api';

const CREATE_ROUTE_NAME = 'aiPromptConfigCreateEdit';

export default {
  name: 'AiPromptConfigUsageDashboard',
  components: {
    PageHeader,
    ActionButton,
    CustomSeparator,
    CustomResultNotFound,
  },
  data() {
    return {
      page: {
        icon: 'fas fa-chart-line',
        title: 'Panel de Uso — PEI GPT',
        routes: ['Inicio', 'Operaciones', 'Consola de Instrucciones IA', 'Panel de Uso'],
      },
      loading: false,
      error: '',
      summary: null,
    };
  },
  computed: {
    successRateLabel() {
      if (
        !this.summary ||
        this.summary.successRate === null ||
        this.summary.successRate === undefined
      ) {
        return 'N/D';
      }
      return `${Math.round(this.summary.successRate * 100)}%`;
    },
    maxTrendValue() {
      if (!this.summary || !this.summary.tendencia.length) {
        return 0;
      }
      return Math.max(...this.summary.tendencia.map((p) => p.totalGenerations));
    },
  },
  methods: {
    async loadSummary() {
      this.loading = true;
      this.error = '';
      try {
        this.summary = await getUsageSummary();
      } catch (e) {
        this.error = e?.response?.data || e.message;
      } finally {
        this.loading = false;
      }
    },
    // Barras simples con CSS (sin librería de gráficas, consistente con el resto de MiPE) --
    // se escala contra la semana con más generaciones, con un mínimo visible para que una
    // semana con pocas generaciones no desaparezca como una barra de 0%.
    trendBarWidth(valor) {
      if (!this.maxTrendValue) {
        return 0;
      }
      return Math.max(4, Math.round((valor / this.maxTrendValue) * 100));
    },
    formatWeek(iso) {
      if (!iso) {
        return '';
      }
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleDateString('es-PR', { year: 'numeric', month: 'short', day: '2-digit' });
    },
    estadoBadgeClass(estado) {
      if (estado === 'Publicado') return 'aiud-badge-published';
      if (estado === 'Borrador') return 'aiud-badge-draft';
      if (estado === 'Archivado') return 'aiud-badge-archived';
      return 'aiud-badge-default';
    },
    // Misma pantalla de detalle que usa Main.vue para "Ver" -- module/sectionArea van en inglés
    // porque así los lee CreateEdit.vue (this.$route.query.module/sectionArea), no en español.
    goToDetail(fila) {
      this.$router.push({
        name: CREATE_ROUTE_NAME,
        query: {
          module: fila.modulo,
          sectionArea: fila.seccionArea,
          sourceId: fila.aiPromptConfigurationId,
          mode: 'view',
        },
      });
    },
  },
  created() {
    this.loadSummary();
  },
};
</script>

<style scoped>
.aiud-hint {
  font-size: 12.5px;
  color: #6b6b6b;
  margin-top: 12px;
}

.aiud-error {
  background: #fdecea;
  color: #c0392b;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12.5px;
  margin-top: 12px;
}

.aiud-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.aiud-stat {
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 10px;
  padding: 14px 22px;
  min-width: 160px;
  text-align: center;
  box-shadow: 0px 3px 6px #00000014;
}

.aiud-stat-value {
  font: bold 26px/1.2 Lato;
  color: #17332c;
}

.aiud-stat-label {
  font-size: 12px;
  color: #6b6b6b;
  margin-top: 4px;
}

.aiud-trend {
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  padding: 14px 18px;
}

.aiud-trend-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}

.aiud-trend-week {
  width: 110px;
  font-size: 11.5px;
  color: #6b6b6b;
  flex: 0 0 auto;
}

.aiud-trend-bar-track {
  flex: 1 1 auto;
  background: #f2f2f2;
  border-radius: 6px;
  height: 14px;
  overflow: hidden;
}

.aiud-trend-bar {
  background: linear-gradient(104deg, #5c58ed 24.303%, #ba65ff 134.64%);
  height: 100%;
  border-radius: 6px;
}

.aiud-trend-count {
  width: 32px;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: #17332c;
  flex: 0 0 auto;
}

.aiud-table-header,
.aiud-table-row {
  display: grid;
  grid-template-columns: 1.2fr 2fr 1fr 0.8fr 0.8fr 0.6fr 0.6fr;
  gap: 8px;
  align-items: center;
  padding: 10px 14px;
}

.aiud-table-header {
  font: bold 11px/1 Lato;
  text-transform: uppercase;
  color: #6b6b6b;
  letter-spacing: 0.3px;
}

.aiud-table-row {
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #17332c;
  cursor: pointer;
}

.aiud-table-row:hover {
  border-color: #5c8f76;
  box-shadow: 0 2px 6px #00000010;
}

.aiud-col-num {
  text-align: center;
  font-weight: 600;
}

.aiud-positive {
  color: #2db782;
}

.aiud-negative {
  color: #c0392b;
}

.aiud-col-note {
  grid-column: 1 / -1;
  font-size: 11px;
  color: #9ba3ad;
  font-style: italic;
  margin-top: 2px;
}

.aiud-status-badge {
  font: bold 10.5px/1 Lato;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 3px 10px;
  border-radius: 999px;
}

.aiud-badge-draft {
  background: #eef0e4;
  color: #6b6f4a;
}

.aiud-badge-published {
  background: #e3f3ea;
  color: #1f7a4d;
}

.aiud-badge-archived {
  background: #f0f0f0;
  color: #6b6b6b;
}

.aiud-badge-default {
  background: #eee;
  color: #666;
}
</style>
